import { useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { LoadingBlock } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
    Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { formatINR, formatINRCompact, formatIndianDateTime } from "@/lib/format";
import {
    Fingerprint, ShieldCheck, TrendingUp, Landmark, CreditCard,
    Receipt, HeartPulse, Sparkles, AlertTriangle, ArrowRight, BadgeIndianRupee, FileDown,
} from "lucide-react";
import { toast } from "sonner";

const SCOPES = [
    { key: "profile", label: "Profile (KYC)" },
    { key: "banks", label: "Bank accounts (Surepass)" },
    { key: "credit", label: "Credit score & loans (OneScore)" },
    { key: "mutual_funds", label: "Mutual funds (AMFI live)" },
    { key: "stocks", label: "Stocks & ETFs (broker API)" },
    { key: "taxes", label: "Tax filings & dues (ITR)" },
    { key: "insurance", label: "Insurance policies" },
];

const inr = formatINR;
const inrCompact = formatINRCompact;

function FreshnessBadge({ freshness, source_id }) {
    const f = (freshness || "").toLowerCase();
    const tone =
        f === "live" ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" :
        f === "24h" ? "bg-amber-500/15 text-amber-300 border-amber-500/30" :
        "bg-muted/40 text-muted-foreground border-border";
    const mocked = (source_id || "").startsWith("MOCKED");
    return (
        <span className="inline-flex items-center gap-1.5 flex-wrap">
            <span className={`text-[10px] uppercase tracking-wider border rounded-full px-2 py-0.5 ${tone}`}>
                {f || "—"}
            </span>
            {mocked && (
                <span className="text-[10px] uppercase tracking-wider border rounded-full px-2 py-0.5 bg-muted/30 text-muted-foreground border-border">
                    Mocked
                </span>
            )}
        </span>
    );
}

function SectionCard({ icon: Icon, title, right, children, testid }) {
    return (
        <div className="rounded-lg border border-border bg-card p-5" data-testid={testid}>
            <div className="flex items-start justify-between gap-4 mb-4">
                <div className="flex items-center gap-3">
                    <Icon className="h-5 w-5 text-primary" strokeWidth={1.5} />
                    <h3 className="font-display text-lg font-semibold">{title}</h3>
                </div>
                {right}
            </div>
            {children}
        </div>
    );
}

export default function PanSnapshot() {
    const [pan, setPan] = useState("");
    const [consent, setConsent] = useState(false);
    const [scope, setScope] = useState(SCOPES.map((s) => s.key));
    const [busy, setBusy] = useState(false);
    const [data, setData] = useState(null);

    const toggleScope = (k) =>
        setScope((s) => (s.includes(k) ? s.filter((x) => x !== k) : [...s, k]));

    const run = async () => {
        if (!pan.trim()) return toast.error("Enter a PAN");
        if (!consent) return toast.error("Consent is mandatory before fetching financial data");
        setBusy(true);
        setData(null);
        try {
            const { data: res } = await api.post("/pan/snapshot", {
                pan: pan.trim().toUpperCase(),
                consent_agreed: consent,
                request_scope: scope,
                preferred_freshness: { mutual_funds: "live", stocks: "live", credit: "live" },
            });
            setData(res);
            if (res.status === "ERROR") {
                toast.error(res.human_message || res.error_code);
            } else {
                toast.success("Snapshot ready");
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || "Snapshot failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="space-y-8" data-testid="pan-snapshot-page">
            <PageHeader
                icon={Fingerprint}
                title="PAN Snapshot"
                subtitle="Consent-gated, multi-source financial profile for any PAN. AMFI NAVs are live; other connectors are stubbed under MOCKED tag until commercial agreements are wired."
            />

            <div className="rounded-lg border border-border bg-card p-6 space-y-5" data-testid="pan-form">
                <div className="grid md:grid-cols-3 gap-4">
                    <div className="md:col-span-1">
                        <label className="label-tag mb-2 block">PAN number</label>
                        <Input
                            value={pan}
                            onChange={(e) => setPan(e.target.value.toUpperCase())}
                            placeholder="ABCDE1234F"
                            maxLength={10}
                            data-testid="pan-input"
                            className="font-mono uppercase tracking-wider"
                        />
                    </div>
                    <div className="md:col-span-2">
                        <label className="label-tag mb-2 block">Data sources to include</label>
                        <div className="grid grid-cols-2 gap-2">
                            {SCOPES.map((s) => (
                                <label key={s.key} className="flex items-center gap-2 text-sm cursor-pointer">
                                    <Checkbox
                                        checked={scope.includes(s.key)}
                                        onCheckedChange={() => toggleScope(s.key)}
                                        data-testid={`scope-${s.key}`}
                                    />
                                    <span>{s.label}</span>
                                </label>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-4 text-sm flex items-start gap-3">
                    <ShieldCheck className="h-5 w-5 text-amber-300 mt-0.5 shrink-0" />
                    <div className="space-y-2">
                        <label className="flex items-start gap-2 cursor-pointer">
                            <Checkbox
                                checked={consent}
                                onCheckedChange={(v) => setConsent(Boolean(v))}
                                data-testid="consent-checkbox"
                                className="mt-0.5"
                            />
                            <span>
                                I authorise <strong>LifeVault by MYTHIVA™</strong> to fetch and display
                                the listed financial data sources for this PAN. I understand the consent is logged,
                                hashed, audit-grade, and revocable from my Security console.
                            </span>
                        </label>
                    </div>
                </div>

                <Button
                    onClick={run}
                    disabled={busy || !pan || !consent}
                    data-testid="pan-run-btn"
                    className="bg-primary text-primary-foreground hover:bg-primary/90"
                >
                    {busy ? "Fetching…" : <>Run snapshot <ArrowRight className="h-4 w-4 ml-2" /></>}
                </Button>
            </div>

            {busy && <LoadingBlock label="Aggregating sources securely…" />}

            {data && data.status === "ERROR" && (
                <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-5 text-sm flex items-start gap-3" data-testid="pan-error">
                    <AlertTriangle className="h-5 w-5 text-destructive mt-0.5" />
                    <div>
                        <div className="font-semibold">{data.error_code}</div>
                        <div className="text-muted-foreground">{data.human_message}</div>
                    </div>
                </div>
            )}

            {data && data.status === "SUCCESS" && (
                <div className="space-y-6" data-testid="pan-result">
                    {/* 1-line summary */}
                    <div className="rounded-lg border border-primary/30 bg-primary/5 p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-3" data-testid="pan-summary-line">
                        <div>
                            <div className="label-tag mb-1">Snapshot</div>
                            <div className="font-display text-lg sm:text-xl font-semibold tracking-tight">
                                {data.human_message}
                            </div>
                            <div className="mt-2 text-xs text-muted-foreground font-mono">
                                consent_hash: {data.consent?.consent_hash?.slice(0, 16)}… · fetched {formatIndianDateTime(data.fetched_at_utc)}
                            </div>
                        </div>
                        <Button
                            onClick={async () => {
                                try {
                                    const res = await api.get(`/pan/consents/${data.consent.consent_id}/pdf`, { responseType: "blob" });
                                    const url = window.URL.createObjectURL(res.data);
                                    const a = document.createElement("a");
                                    a.href = url;
                                    a.download = `lifevault-pan-snapshot-${data.pan}.pdf`;
                                    document.body.appendChild(a);
                                    a.click();
                                    a.remove();
                                    window.URL.revokeObjectURL(url);
                                } catch (e) {
                                    toast.error(e.response?.data?.detail || "PDF download failed");
                                }
                            }}
                            data-testid="pan-pdf-download"
                            className="bg-primary text-primary-foreground hover:bg-primary/90 shrink-0"
                        >
                            <FileDown className="h-4 w-4 mr-2" /> Download PDF
                        </Button>
                    </div>

                    {/* Smart Snapshot */}
                    <SectionCard icon={Sparkles} title="Smart Snapshot" testid="smart-snapshot" right={
                        <Badge variant="outline" className="font-mono">{data.smart_snapshot.risk} risk</Badge>
                    }>
                        <div className="grid md:grid-cols-3 gap-4">
                            <div>
                                <div className="label-tag">12-month return</div>
                                <div className="font-mono text-2xl font-bold text-primary mt-1">
                                    {data.smart_snapshot.twelve_month_return_pct > 0 ? "+" : ""}
                                    {data.smart_snapshot.twelve_month_return_pct}%
                                </div>
                            </div>
                            <div>
                                <div className="label-tag">Investable</div>
                                <div className="font-mono text-2xl font-bold mt-1">{inr(data.smart_snapshot.investable_inr)}</div>
                            </div>
                            <div>
                                <div className="label-tag">CIBIL</div>
                                <div className="font-mono text-2xl font-bold mt-1">{data.smart_snapshot.credit_score || "—"}</div>
                            </div>
                        </div>
                        <div className="mt-4 text-sm flex items-start gap-2">
                            <ArrowRight className="h-4 w-4 text-primary mt-0.5" />
                            <span>{data.smart_snapshot.next_action}</span>
                        </div>
                    </SectionCard>

                    {/* Alerts */}
                    {data.alerts_and_recommendations?.length > 0 && (
                        <SectionCard icon={AlertTriangle} title="Alerts & Recommendations" testid="pan-alerts">
                            <ul className="space-y-2">
                                {data.alerts_and_recommendations.map((a, i) => (
                                    <li key={i} className="text-sm flex items-start gap-3" data-testid={`alert-${i}`}>
                                        <Badge variant="outline" className={
                                            a.priority === "HIGH" ? "border-destructive/40 text-destructive" :
                                            a.priority === "MEDIUM" ? "border-amber-500/40 text-amber-300" :
                                            "border-border text-muted-foreground"
                                        }>{a.type}</Badge>
                                        <span>{a.message}</span>
                                    </li>
                                ))}
                            </ul>
                        </SectionCard>
                    )}

                    {/* Sections */}
                    <Accordion type="multiple" defaultValue={["banks", "credit", "mf", "stocks"]} className="space-y-3">
                        {/* Banks */}
                        <AccordionItem value="banks" className="border border-border rounded-lg bg-card" data-testid="acc-banks">
                            <AccordionTrigger className="px-5 hover:no-underline">
                                <span className="flex items-center gap-3">
                                    <Landmark className="h-5 w-5 text-primary" />
                                    <span className="font-display text-lg font-semibold">Banks</span>
                                    <span className="text-sm text-muted-foreground font-mono">
                                        {inr(data.banks.total_bank_balance.value)}
                                    </span>
                                    <FreshnessBadge {...data.banks.total_bank_balance} />
                                </span>
                            </AccordionTrigger>
                            <AccordionContent className="px-5 pb-5 space-y-2">
                                {data.banks.linked_bank_accounts.length === 0 && (
                                    <div className="text-sm text-muted-foreground">No linked bank accounts.</div>
                                )}
                                {data.banks.linked_bank_accounts.map((b, i) => (
                                    <div key={i} className="flex items-center justify-between text-sm border-b border-border/50 last:border-0 py-2">
                                        <div>
                                            <div className="font-semibold">{b.bank_name}</div>
                                            <div className="text-xs text-muted-foreground font-mono">{b.masked_account}</div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <Badge variant={b.pan_link_status === "LINKED" ? "default" : "outline"}>{b.pan_link_status}</Badge>
                                            <FreshnessBadge {...b} />
                                        </div>
                                    </div>
                                ))}
                            </AccordionContent>
                        </AccordionItem>

                        {/* Credit */}
                        <AccordionItem value="credit" className="border border-border rounded-lg bg-card" data-testid="acc-credit">
                            <AccordionTrigger className="px-5 hover:no-underline">
                                <span className="flex items-center gap-3">
                                    <CreditCard className="h-5 w-5 text-primary" />
                                    <span className="font-display text-lg font-semibold">Credit</span>
                                    <span className="text-sm text-muted-foreground font-mono">
                                        {data.credit.cibil_score.value || "—"} ({data.credit.cibil_score.tier || "—"})
                                    </span>
                                    <FreshnessBadge {...data.credit.cibil_score} />
                                </span>
                            </AccordionTrigger>
                            <AccordionContent className="px-5 pb-5 space-y-3">
                                <div className="text-sm">
                                    Utilization: <span className="font-mono">{data.credit.credit_utilization_pct.value ?? "—"}%</span>
                                </div>
                                {data.credit.open_loans.length > 0 ? data.credit.open_loans.map((l, i) => (
                                    <div key={i} className="flex items-center justify-between text-sm border-t border-border/50 pt-2">
                                        <div>
                                            <div className="font-semibold capitalize">{l.type.replace("_", " ")} · {l.lender}</div>
                                            <div className="text-xs text-muted-foreground font-mono">EMI {inr(l.emi)}</div>
                                        </div>
                                        <div className="font-mono">{inr(l.amount_outstanding)}</div>
                                    </div>
                                )) : <div className="text-sm text-muted-foreground">No open loans.</div>}
                            </AccordionContent>
                        </AccordionItem>

                        {/* Mutual Funds */}
                        <AccordionItem value="mf" className="border border-border rounded-lg bg-card" data-testid="acc-mf">
                            <AccordionTrigger className="px-5 hover:no-underline">
                                <span className="flex items-center gap-3">
                                    <TrendingUp className="h-5 w-5 text-primary" />
                                    <span className="font-display text-lg font-semibold">Mutual Funds</span>
                                    <span className="text-sm text-muted-foreground font-mono">
                                        {inr(data.investments.mutual_funds.aggregate_value.value)}
                                    </span>
                                    {data.investments.mutual_funds.holdings[0] && (
                                        <FreshnessBadge {...data.investments.mutual_funds.holdings[0]} />
                                    )}
                                </span>
                            </AccordionTrigger>
                            <AccordionContent className="px-5 pb-5 space-y-2">
                                {data.investments.mutual_funds.holdings.slice(0, 5).map((h, i) => (
                                    <div key={i} className="flex items-center justify-between text-sm border-b border-border/50 last:border-0 py-2 gap-3">
                                        <div className="min-w-0">
                                            <div className="font-semibold truncate">{h.scheme_name}</div>
                                            <div className="text-xs text-muted-foreground font-mono">
                                                {h.amc} · NAV ₹{h.nav}
                                            </div>
                                        </div>
                                        <div className="text-right shrink-0">
                                            <div className="font-mono">{inr(h.current_value)}</div>
                                            <div className={`text-xs font-mono ${h.gain_loss_percent >= 0 ? "text-emerald-300" : "text-destructive"}`}>
                                                {h.gain_loss_percent >= 0 ? "+" : ""}{h.gain_loss_percent}%
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </AccordionContent>
                        </AccordionItem>

                        {/* Stocks */}
                        <AccordionItem value="stocks" className="border border-border rounded-lg bg-card" data-testid="acc-stocks">
                            <AccordionTrigger className="px-5 hover:no-underline">
                                <span className="flex items-center gap-3">
                                    <BadgeIndianRupee className="h-5 w-5 text-primary" />
                                    <span className="font-display text-lg font-semibold">Stocks & ETFs</span>
                                    <span className="text-sm text-muted-foreground font-mono">
                                        {inr(data.investments.stocks_and_etfs.aggregate_value.value)}
                                    </span>
                                    {data.investments.stocks_and_etfs.holdings[0] && (
                                        <FreshnessBadge {...data.investments.stocks_and_etfs.holdings[0]} />
                                    )}
                                </span>
                            </AccordionTrigger>
                            <AccordionContent className="px-5 pb-5 space-y-2">
                                {data.investments.stocks_and_etfs.holdings.map((h, i) => (
                                    <div key={i} className="flex items-center justify-between text-sm border-b border-border/50 last:border-0 py-2">
                                        <div>
                                            <div className="font-semibold font-mono">{h.symbol}.{h.exchange}</div>
                                            <div className="text-xs text-muted-foreground font-mono">{h.qty} qty @ ₹{h.lavg_price}</div>
                                        </div>
                                        <div className="text-right">
                                            <div className="font-mono">{inr(h.market_value)}</div>
                                            <div className={`text-xs font-mono ${h.unrealized_pl_pct >= 0 ? "text-emerald-300" : "text-destructive"}`}>
                                                {h.unrealized_pl_pct >= 0 ? "+" : ""}{h.unrealized_pl_pct}%
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </AccordionContent>
                        </AccordionItem>

                        {/* Taxes */}
                        <AccordionItem value="taxes" className="border border-border rounded-lg bg-card" data-testid="acc-taxes">
                            <AccordionTrigger className="px-5 hover:no-underline">
                                <span className="flex items-center gap-3">
                                    <Receipt className="h-5 w-5 text-primary" />
                                    <span className="font-display text-lg font-semibold">Taxes</span>
                                    <span className="text-sm text-muted-foreground font-mono">
                                        ITR {data.taxes.latest_itr_filing_year.value || "—"}
                                    </span>
                                </span>
                            </AccordionTrigger>
                            <AccordionContent className="px-5 pb-5 text-sm space-y-2">
                                <div>Last filing year: <span className="font-mono">{data.taxes.latest_itr_filing_year.value || "—"}</span></div>
                                <div>Pending dues: <span className="font-mono">{inr(data.taxes.pending_tax_dues.value)}</span></div>
                            </AccordionContent>
                        </AccordionItem>

                        {/* Insurance */}
                        <AccordionItem value="insurance" className="border border-border rounded-lg bg-card" data-testid="acc-insurance">
                            <AccordionTrigger className="px-5 hover:no-underline">
                                <span className="flex items-center gap-3">
                                    <HeartPulse className="h-5 w-5 text-primary" />
                                    <span className="font-display text-lg font-semibold">Insurance</span>
                                    <span className="text-sm text-muted-foreground font-mono">
                                        {data.insurance.policies.length} policies
                                    </span>
                                </span>
                            </AccordionTrigger>
                            <AccordionContent className="px-5 pb-5 space-y-2">
                                {data.insurance.policies.map((p, i) => (
                                    <div key={i} className="flex items-center justify-between text-sm border-b border-border/50 last:border-0 py-2">
                                        <div>
                                            <div className="font-semibold">{p.insurer}</div>
                                            <div className="text-xs text-muted-foreground capitalize">{p.policy_type} · {p.status}</div>
                                        </div>
                                        <div className="text-right">
                                            <div className="font-mono">SA {inr(p.sum_assured)}</div>
                                            <div className="text-xs text-muted-foreground font-mono">Prem {inr(p.premium)}</div>
                                        </div>
                                    </div>
                                ))}
                            </AccordionContent>
                        </AccordionItem>
                    </Accordion>

                    {/* Source summary */}
                    <div className="rounded-lg border border-border bg-card/60 p-5" data-testid="source-summary">
                        <div className="label-tag mb-3">Sources used</div>
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                            {data.source_summary.sources_used.map((s, i) => (
                                <div key={i} className="text-xs flex items-center justify-between border border-border rounded-md px-3 py-2">
                                    <span className="font-mono truncate">{s.id || "—"}</span>
                                    <span className="text-muted-foreground">{s.status}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
